import { get, getDatabase, ref, remove, set, update, push } from 'firebase/database';

export interface LocationCategoryOption {
    key: string;
    name: string;
    parent?: string;
}

export interface GlobalLocationCategories {
    labels?: {
        category1?: string;
        category2?: string;
        category3?: string;
        category4?: string;
        category5?: string;
    };
    category1?: { [key: string]: { name: string } };
    category2?: { [key: string]: { name: string; parent: string } };
    category3?: { [key: string]: { name: string; parent: string } };
    category4?: { [key: string]: { name: string; parent: string } };
    category5?: { [key: string]: { name: string; parent: string } };
}

/**
 * Fetch all location categories from Firebase
 */
export async function fetchLocationCategories(): Promise<GlobalLocationCategories | null> {
    try {
        const db = getDatabase();
        const categoryRef = ref(db, 'locationCategories');
        const snapshot = await get(categoryRef);

        if (snapshot.exists()) {
            return snapshot.val() as GlobalLocationCategories;
        }
        return null;
    } catch (error) {
        console.error('Error fetching location categories:', error);
        throw error;
    }
}

/**
 * Get ALL options for a specific category level (unfiltered)
 */
export function getAllCategoryOptions(
    categories: GlobalLocationCategories,
    level: 1 | 2 | 3 | 4 | 5
): LocationCategoryOption[] {
    const categoryKey = `category${level}` as keyof GlobalLocationCategories;
    const categoryData = categories[categoryKey];

    if (!categoryData || typeof categoryData === 'string') {
        return [];
    }

    const options = Object.entries(categoryData).map(([key, value]) => ({
        key,
        name: value.name,
        parent: 'parent' in value ? value.parent : undefined,
    }));

    return options.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
}

/**
 * Get options for a specific category level, filtered by parent if applicable
 */
export function getCategoryOptions(
    categories: GlobalLocationCategories,
    level: 1 | 2 | 3 | 4 | 5,
    parentKey?: string
): LocationCategoryOption[] {
    const categoryKey = `category${level}` as keyof GlobalLocationCategories;
    const categoryData = categories[categoryKey];

    if (!categoryData || typeof categoryData === 'string') {
        return [];
    }

    const options = Object.entries(categoryData).map(([key, value]) => ({
        key,
        name: value.name,
        parent: 'parent' in value ? value.parent : undefined,
    }));

    // For level 1, return all options
    if (level === 1) {
        return options.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }

    // For other levels, if no parent key provided, return all
    if (parentKey === undefined) {
        return options.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }

    // Filter by parent
    return options
        .filter(option => option.parent === parentKey)
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
}

/**
 * Get the label for a specific category level
 */
export function getCategoryLabel(
    categories: GlobalLocationCategories,
    level: 1 | 2 | 3 | 4 | 5
): string {
    const defaultLabels = {
        1: 'Kategori 1',
        2: 'Kategori 2',
        3: 'Kategori 3',
        4: 'Kategori 4',
        5: 'Kategori 5',
    };

    return categories.labels?.[`category${level}`] || defaultLabels[level];
}

/**
 * Add a new category
 */
export async function addCategory(
    level: 1 | 2 | 3 | 4 | 5,
    name: string,
    parentKey?: string
): Promise<string> {
    try {
        const db = getDatabase();
        const categoryKey = `category${level}`;
        
        // Use Firebase push to generate a unique key
        const categoryLevelRef = ref(db, `locationCategories/${categoryKey}`);
        const newCategoryRef = push(categoryLevelRef);
        
        if (!newCategoryRef.key) {
            throw new Error('Failed to generate category key');
        }
        
        const data: { name: string; parent?: string } = { name };
        
        if (level > 1 && parentKey) {
            data.parent = parentKey;
        }
        
        await set(newCategoryRef, data);
        return newCategoryRef.key;
    } catch (error) {
        console.error('Error adding category:', error);
        throw error;
    }
}

/**
 * Update a category name
 */
export async function updateCategoryName(
    level: 1 | 2 | 3 | 4 | 5,
    key: string,
    newName: string
): Promise<void> {
    try {
        const db = getDatabase();
        const categoryKey = `category${level}`;
        const categoryRef = ref(db, `locationCategories/${categoryKey}/${key}/name`);
        
        await set(categoryRef, newName);
    } catch (error) {
        console.error('Error updating category name:', error);
        throw error;
    }
}

/**
 * Delete a category
 */
export async function deleteCategory(
    level: 1 | 2 | 3 | 4 | 5,
    key: string
): Promise<void> {
    try {
        const db = getDatabase();
        const categoryKey = `category${level}`;
        const categoryRef = ref(db, `locationCategories/${categoryKey}/${key}`);
        
        await remove(categoryRef);
    } catch (error) {
        console.error('Error deleting category:', error);
        throw error;
    }
}

/**
 * Update category level label
 */
export async function updateCategoryLabel(
    level: 1 | 2 | 3 | 4 | 5,
    label: string
): Promise<void> {
    try {
        const db = getDatabase();
        const labelRef = ref(db, `locationCategories/labels/category${level}`);
        
        await set(labelRef, label);
    } catch (error) {
        console.error('Error updating category label:', error);
        throw error;
    }
}

/**
 * Check if a category can be deleted (checks both building usage and child categories)
 */
export async function checkCategoryDeletion(
    level: 1 | 2 | 3 | 4 | 5,
    key: string,
    categories: GlobalLocationCategories
): Promise<{
    canDelete: boolean;
    buildingUsage: { count: number; buildingNames: string[] };
    childCategories: { count: number; childNames: string[] };
}> {
    try {
        // Check building usage
        const db = getDatabase();
        const summariesRef = ref(db, 'buildingsSummary');
        const snapshot = await get(summariesRef);

        const buildingNames: string[] = [];
        const categoryKey = `category${level}`;

        console.log(`Checking deletion for ${categoryKey} with key: ${key}`);

        if (snapshot.exists()) {
            const summaries = snapshot.val();
            let buildingsChecked = 0;
            
            Object.entries(summaries).forEach(([buildingKey, summary]: [string, any]) => {
                buildingsChecked++;
                
                // Check if this building has location categories
                if (summary.locationCategories && summary.locationCategories[categoryKey]) {
                    const buildingCategoryKey = summary.locationCategories[categoryKey].key;
                    
                    // Convert both to string for comparison (handles String vs string type differences)
                    const normalizedBuildingKey = String(buildingCategoryKey);
                    const normalizedSearchKey = String(key);
                    
                    console.log(`Building "${summary.name}": ${categoryKey}.key = "${normalizedBuildingKey}" vs "${normalizedSearchKey}"`);
                    
                    if (normalizedBuildingKey === normalizedSearchKey) {
                        buildingNames.push(summary.name || 'Namnlös byggnad');
                        console.log(`  ✓ Match found!`);
                    }
                }
            });
            
            console.log(`Checked ${buildingsChecked} buildings, found ${buildingNames.length} using this category`);
        } else {
            console.log('No building summaries found in database');
        }

        // Check for child categories (only if not level 5)
        const childNames: string[] = [];
        if (level < 5) {
            const nextLevel = (level + 1) as 2 | 3 | 4 | 5;
            const nextLevelKey = `category${nextLevel}` as keyof GlobalLocationCategories;
            const nextLevelData = categories[nextLevelKey];

            if (nextLevelData && typeof nextLevelData !== 'string') {
                Object.entries(nextLevelData).forEach(([childKey, childValue]) => {
                    if ('parent' in childValue && String(childValue.parent) === String(key)) {
                        childNames.push(childValue.name);
                    }
                });
            }
            
            console.log(`Found ${childNames.length} child categories`);
        }

        const canDelete = buildingNames.length === 0 && childNames.length === 0;

        console.log(`Can delete: ${canDelete}`);

        return {
            canDelete,
            buildingUsage: {
                count: buildingNames.length,
                buildingNames,
            },
            childCategories: {
                count: childNames.length,
                childNames,
            },
        };
    } catch (error) {
        console.error('Error checking category deletion:', error);
        throw error;
    }
}

/**
 * Check if a category is used by any buildings
 * @deprecated Use checkCategoryDeletion instead for more complete validation
 */
export async function checkCategoryUsage(
    level: 1 | 2 | 3 | 4 | 5,
    key: string
): Promise<{ isUsed: boolean; count: number; buildingNames: string[] }> {
    try {
        const db = getDatabase();
        const summariesRef = ref(db, 'buildingSummaries');
        const snapshot = await get(summariesRef);

        if (!snapshot.exists()) {
            return { isUsed: false, count: 0, buildingNames: [] };
        }

        const summaries = snapshot.val();
        const buildingNames: string[] = [];
        const categoryKey = `category${level}`;

        Object.values(summaries).forEach((summary: any) => {
            if (summary.locationCategories?.[categoryKey]?.key === key) {
                buildingNames.push(summary.name || 'Namnlös byggnad');
            }
        });

        return {
            isUsed: buildingNames.length > 0,
            count: buildingNames.length,
            buildingNames,
        };
    } catch (error) {
        console.error('Error checking category usage:', error);
        throw error;
    }
}

/**
 * Get all child categories for a given parent
 */
export function getChildCategories(
    categories: GlobalLocationCategories,
    level: 2 | 3 | 4 | 5,
    parentKey: string
): LocationCategoryOption[] {
    return getCategoryOptions(categories, level, parentKey);
}

